import { useCallback, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";
import { Dayjs } from "dayjs";

import { useVisible, UseVisibleReturn } from "@/hooks/useVisible";
import { useDialog } from "@/hooks/useDialog";
import { useScanImageSchema } from "@/features/talent/hooks/useScanImageSchema";
import { useGetLanguageTestList, useGetSpecificSkillCertificateList } from "@/api/services/global/master";
import { useMe } from "@/api/services/main/auth";
import {
  createTalentApi,
  fetchCheckPassportDuplicateApi,
  fetchTalentExistsApi,
  generateNoteApi,
  generateScanImageTalentApi,
  updateTalentApi,
} from "@/api/services/main/talent";
import {
  TGenerateScanImageResDto,
  TUnifiedScanResDto,
} from "@/api/services/main/talent/dtos/generate-scan-image.dto";
import { TGetTalentExistsDto, TGetTalentExistsResDto } from "@/api/services/main/talent/dtos/get-talent-exists.dto";
import { TCheckPassportDuplicateDto, TCheckPassportDuplicateResDto } from "@/api/services/main/talent/dtos/check-passport-duplicate.dto";
import { TMasterLanguageTest } from "@/api/entities/master-language-test.entity";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { TExtendFile } from "@/types/file";
import { MESSAGE_EMPTY_DATA_FROM_SCAN } from "@/features/talent/constants/scan";
import { getScanPreviewType } from "@/features/talent/constants/scan-accept";
import { buildRelatedFileTypes } from "@/features/talent/utils/build-related-file-types";
import { TALENT_SCHEMA_LABELS } from "@/features/talent/constants/schema-labels";
import { convertTalentFormDataReq } from "@/features/talent/utils/convertTalentFormData";
import { dayjs } from "@/utils/dayjs";
import { DATE_FORMAT_FOR_API } from "@/constants/datetime";
import { toast } from "@/utils/toast";
import { API_SUCCESS_MESSAGE, API_UNKNOWN_ERROR_MESSAGE } from "@/constants/common";
import { showError } from "@/utils/error";
import { parseMemoToObject } from "@/utils/parse-labeled-input";
import { INVALID_FILE_SCAN_TALENT } from "@/constants/invalids";
import { EForeignLanguage, GENDER } from "@/types/enum";

/** Tab key for the result panel — drives both Tabs UI and conditional render. */
export type ScanTab = "all" | "resume" | "card" | "myNumberCard";

/** Safely parse JSON — returns value as-is if already parsed, null on failure */
const safeJsonParse = <T,>(value: unknown): T | null => {
  if (!value) return null;
  if (typeof value !== "string") return value as T;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

type TWorkExperienceRaw = {
  startDate?: string | number | Date | Dayjs | null;
  endDate?: string | number | Date | Dayjs | null;
  [key: string]: unknown;
};

const FOREIGN_LANGUAGE_NAME_MAP: Record<string, number> = {
  英語: EForeignLanguage.ENGLISH,
  中国語: EForeignLanguage.CHINESE,
};

const resolveLanguageSkillIds = (
  parsed: { language?: number | null; content?: string | null }[],
  rawLanguageNames?: string[],
): { language: number | null; content: string | null }[] => {
  return parsed.map((s, i) => {
    if (s.language && s.language > 0) return { ...s, language: s.language, content: s.content ?? null };
    const rawName = rawLanguageNames?.[i];
    const resolvedId = rawName ? (FOREIGN_LANGUAGE_NAME_MAP[rawName] ?? EForeignLanguage.OTHER) : null;
    return { ...s, language: resolvedId, content: s.content ?? null };
  });
};

type TScanResponseData = TGenerateScanImageResDto & {
  birthday?: string | Dayjs | null;
  relatedFiles?: File[];
  relatedFileTypes?: string[];
};

/**
 * Hydrate a flat scan-response payload into a TTalentForm: parses dates,
 * unwraps stringified JSON columns, and resolves language-test/skill ids.
 * Used for resume / note / mergedTalentForm — all share the same shape.
 */
const hydrateTalentData = (data: TGenerateScanImageResDto): TScanResponseData => {
  const languageTestLevelIds: (number | null)[] = [];
  if (Array.isArray(data.languageCertificates)) {
    (data.languageCertificates as { languageTestLevelId?: string | number; languageTestId?: string | number }[]).forEach((cert) => {
      if (cert?.languageTestLevelId) {
        languageTestLevelIds.push(Number(cert.languageTestLevelId));
      } else if (cert?.languageTestId) {
        languageTestLevelIds.push(-Number(cert.languageTestId));
      }
    });
  }

  const specificSkillCertificateIds: (number | null)[] = [];
  if (Array.isArray(data.specificSkillCertificates)) {
    (data.specificSkillCertificates as { id?: number; specificSkillCertificate?: { id?: number }; specificSkillCertificateId?: number }[]).forEach(
      (cert) => {
        const certId = cert?.specificSkillCertificate?.id ?? cert?.specificSkillCertificateId ?? cert?.id;
        if (certId) {
          specificSkillCertificateIds.push(Number(certId));
        }
      },
    );
  }

  return {
    ...data,
    gender: Number(data.gender) === GENDER.OTHER || data.gender === "その他" ? undefined : data.gender,
    birthday: data.birthday ? dayjs(data.birthday) : null,
    specifiedSkillFirstStartDate: data.specifiedSkillFirstStartDate ? dayjs(data.specifiedSkillFirstStartDate) : null,
    periodOfStay: data.periodOfStay ? dayjs(data.periodOfStay) : null,
    passportExpiryDate: data.passportExpiryDate ? dayjs(data.passportExpiryDate) : null,
    interviewDate: data.interviewDate ? dayjs(data.interviewDate) : null,
    workExperiences: data.workExperiences
      ? (safeJsonParse<TWorkExperienceRaw[]>(data.workExperiences)?.map((exp) => ({
          ...exp,
          countryId: exp.countryId ? Number(exp.countryId) : null,
          startDate: exp.startDate ? dayjs(exp.startDate) : null,
          endDate: exp.endDate ? dayjs(exp.endDate) : null,
        })) ?? null)
      : null,
    educationBackground: Array.isArray(data.educationBackground)
      ? (data.educationBackground as { enrollmentDate?: string | null; graduationDate?: string | null }[]).map((edu) => ({
          ...edu,
          enrollmentDate: edu.enrollmentDate ? dayjs(edu.enrollmentDate) : null,
          graduationDate: edu.graduationDate ? dayjs(edu.graduationDate) : null,
        }))
      : null,
    fullName: data.fullName,
    heldQualifications: data.heldQualifications ? safeJsonParse(data.heldQualifications) : null,
    languageSkills: data.languageSkills
      ? resolveLanguageSkillIds(safeJsonParse(data.languageSkills) ?? [], (data.raw as { language_skills?: string[] } | undefined)?.language_skills)
      : null,
    languageTestLevelIds: languageTestLevelIds.length > 0 ? languageTestLevelIds : null,
    specificSkillCertificates: specificSkillCertificateIds.length > 0 ? specificSkillCertificateIds : null,
  } as unknown as TScanResponseData;
};

// `currentTalent` (IROHANA_MATCH_DEV-281): when modal opened from talent detail
// the save action becomes an update against this id instead of a create.
// Null/undefined keeps the original create-talent flow used from talent list.
export function useScanImageState(modal: UseVisibleReturn, currentTalent?: TTalentForm | null) {
  const { scanImageSchema } = useScanImageSchema();
  const { data: languageTestData } = useGetLanguageTestList();
  const { data: specificSkillCertificateData } = useGetSpecificSkillCertificateList();
  const { data: user } = useMe();
  const navigate = useNavigate();

  const methods = useForm({
    defaultValues: { memo: "" },
    resolver: yupResolver(scanImageSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });
  const { control, watch, getValues, setValue, reset } = methods;

  const [isResultData, setResultData] = useState(false);
  const [defaultTalentValue, setDefaultTalentValue] = useState<TTalentForm | null>();
  const [dataCreateTalent, setDataCreateTalent] = useState<TTalentForm>();
  const [validateForm, setValidateForm] = useState<(() => Promise<boolean>) | null>(null);
  const [file, setFile] = useState<File[] | null>(null);
  const [isSubmit, setIsSubmit] = useState(false);
  const [previewFile, setPreviewFile] = useState<(string | File)[]>([]);
  const [typePreviewFile, setTypePreviewFile] = useState<string[]>([]);
  const [duplicateTalents, setDuplicateTalents] = useState<TGetTalentExistsDto[]>([]);
  const [duplicatePassports, setDuplicatePassports] = useState<TCheckPassportDuplicateDto[]>([]);
  const [pendingPassportNumber, setPendingPassportNumber] = useState<string>("");
  const [processingProgress, setProcessingProgress] = useState(0);
  // Per-file OCR meta from the unified scan stream (updated by progress chunks).
  // Phase 5 will surface this via a Stepper UI; Phase 4 only stores it.
  const [currentFile, setCurrentFile] = useState<string | undefined>(undefined);
  const [currentFileIndex, setCurrentFileIndex] = useState<number | undefined>(undefined);
  const [totalFiles, setTotalFiles] = useState<number | undefined>(undefined);
  // Unified scan response buckets (kept verbatim for tab switching).
  const [scanResult, setScanResult] = useState<TUnifiedScanResDto | null>(null);
  // Flips true after the first successful scan within this modal session so the
  // result banner can append the 2回目以降 reminder copy (IROHANA_MATCH_DEV-281).
  const [hasScannedBefore, setHasScannedBefore] = useState(false);
  const [activeTab, setActiveTab] = useState<ScanTab>("all");
  const controllerRef = useRef<AbortController | null>(null);

  const confirmLeaveModal = useVisible();
  const duplicateTalentModal = useDialog();
  const duplicatePassportModal = useDialog();

  const memoWatchField = watch("memo");
  // Was tied to the legacy fileType selector; now the dropzone is always
  // visible and result preview is gated on whether files were submitted.
  const isShowDropZoneUpdateFile = !!file && file.length > 0;
  const otherNote = getValues("memo");

  const handleChangeFiles = async (value: TExtendFile | TExtendFile[] | null) => {
    const files = [value ?? []].flat();
    if (!files[0]) {
      setFile(null);
      return;
    }
    setFile(files);
  };

  const onClose = () => {
    if (getValues("memo") || file) {
      confirmLeaveModal.open();
    } else {
      confirmClose();
    }
  };

  const confirmClose = () => {
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }
    confirmLeaveModal.close();
    setIsSubmit(false);
    reset();
    setPreviewFile([]);
    setTypePreviewFile([]);
    setFile(null);
    setResultData(false);
    setDefaultTalentValue(null);
    setDataCreateTalent(undefined);
    setScanResult(null);
    setHasScannedBefore(false);
    setActiveTab("all");
    setCurrentFile(undefined);
    setCurrentFileIndex(undefined);
    setTotalFiles(undefined);
    setValue("memo", "");
    modal.close();
  };

  /**
   * Cancel an in-flight scan. Aborts the fetch (frees the FE reader) but the
   * BE may still complete the OCR + OpenAI work — that's fine, cost is bounded.
   */
  const cancelScan = useCallback(() => {
    controllerRef.current?.abort();
    controllerRef.current = null;
    setIsSubmit(false);
    setProcessingProgress(0);
    setCurrentFile(undefined);
    setCurrentFileIndex(undefined);
    setTotalFiles(undefined);
  }, []);

  const checkEmptyData = (memo: string) => {
    const isEmptyMemo = memo === "";
    const isEmptyFile = !file || file.length === 0;
    if (isEmptyMemo && isEmptyFile) {
      toast.error(MESSAGE_EMPTY_DATA_FROM_SCAN);
      return false;
    }
    return true;
  };

  const onSubmitNote = async (memo: string) => {
    try {
      setIsSubmit(true);
      setProcessingProgress(0);
      controllerRef.current = new AbortController();

      const parsedMemoObject = parseMemoToObject(memo, TALENT_SCHEMA_LABELS);

      const { data } = await generateNoteApi(parsedMemoObject, (progress) => setProcessingProgress(progress), controllerRef.current.signal);
      if (!data) {
        setIsSubmit(false);
        toast.error(API_UNKNOWN_ERROR_MESSAGE);
        return;
      }

      const talentData = hydrateTalentData(data as TGenerateScanImageResDto);
      setDefaultTalentValue(talentData as unknown as TTalentForm);
      setDataCreateTalent(talentData as unknown as TTalentForm);
      setScanResult({ note: data as TGenerateScanImageResDto, mergedTalentForm: data as TGenerateScanImageResDto });
      setActiveTab("all");
      setResultData(true);
      setHasScannedBefore(true);
      setIsSubmit(false);
      setProcessingProgress(0);
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") return;
      setIsSubmit(false);
      setResultData(false);
      setDefaultTalentValue(null);
      setProcessingProgress(0);
    }
  };

  /**
   * Unified scan handler (IROHANA_MATCH_DEV-280): single endpoint returns
   * { card?, resume?, note?, mergedTalentForm }. The 全項目 tab uses
   * mergedTalentForm; per-tab raw buckets drive the 履歴書 / 在留カード views.
   */
  const onSubmitScan = async (memo: string) => {
    if (!file || file.length === 0) {
      toast.error(MESSAGE_EMPTY_DATA_FROM_SCAN);
      return;
    }
    setIsSubmit(true);
    setProcessingProgress(0);
    controllerRef.current = new AbortController();
    try {
      const parsedMemoObject = parseMemoToObject(memo, TALENT_SCHEMA_LABELS);
      const { data } = (await generateScanImageTalentApi(
        file,
        parsedMemoObject,
        (chunk) => {
          if (chunk.progress !== undefined) setProcessingProgress(chunk.progress);
          if (chunk.currentFile !== undefined) setCurrentFile(chunk.currentFile);
          if (chunk.fileIndex !== undefined) setCurrentFileIndex(chunk.fileIndex);
          if (chunk.totalFiles !== undefined) setTotalFiles(chunk.totalFiles);
        },
        controllerRef.current.signal,
      )) as { data: TUnifiedScanResDto | null };

      if (!data || (!data.card && !data.myNumberCard && !data.resume && !data.note && !data.mergedTalentForm)) {
        toast.error(INVALID_FILE_SCAN_TALENT);
        setIsSubmit(false);
        setResultData(false);
        setDefaultTalentValue(null);
        setDataCreateTalent(undefined);
        setFile(null);
        return;
      }

      // Prefer mergedTalentForm for prefill; fall back to whichever bucket exists.
      // v2.7+ (IROHANA_MATCH_DEV-281): mergedTalentForm is already scoped to a
      // single picked person when users upload multiple files containing
      // different persons. No FE-side filtering needed — buckets arrive clean.
      if (process.env.NODE_ENV !== "production" && data.multiPersonDetected) {
        // eslint-disable-next-line no-console
        console.warn(
          `[talent-scan] multi-person upload detected — kept pickedIndex=${data.pickedIndex ?? 0}, files=${data.perFileExtraction?.length ?? 0}`,
        );
      }
      const merged = data.mergedTalentForm ?? data.resume ?? data.card ?? (data.myNumberCard as unknown as TGenerateScanImageResDto) ?? data.note;
      if (!merged) {
        toast.error(INVALID_FILE_SCAN_TALENT);
        setIsSubmit(false);
        return;
      }

      const hydrated = hydrateTalentData(merged);
      const talentData: TScanResponseData = {
        ...hydrated,
        relatedFiles: file,
        // AI-classified per file (IROHANA_MATCH_DEV-280). Helper guarantees
        // length match with `file` so the BE INSERT never receives NaN.
        relatedFileTypes: buildRelatedFileTypes(file, data.filesClassification),
      };
      setDefaultTalentValue(talentData as unknown as TTalentForm);
      setDataCreateTalent(talentData as unknown as TTalentForm);
      setScanResult(data);
      setHasScannedBefore(true);
      setActiveTab("all");

      const previewFiles: (string | File)[] = [];
      const typePreviewFiles: string[] = [];
      file.forEach((item) => {
        const previewType = getScanPreviewType(item.type);
        if (previewType === "tiff") {
          previewFiles.push(item);
          typePreviewFiles.push("tiff");
        } else if (previewType === "image") {
          previewFiles.push(URL.createObjectURL(item));
          typePreviewFiles.push("image");
        } else if (previewType === "pdf") {
          previewFiles.push(URL.createObjectURL(item));
          typePreviewFiles.push("pdf");
        } else if (previewType === "docx") {
          previewFiles.push(item);
          typePreviewFiles.push("docx");
        } else {
          previewFiles.push(item);
          typePreviewFiles.push("office");
        }
      });
      setPreviewFile(previewFiles);
      setTypePreviewFile(typePreviewFiles);
      setResultData(true);
      setIsSubmit(false);
      setProcessingProgress(0);
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") return;
      if (e instanceof Error && e.name === "CanceledError") return;
      setIsSubmit(false);
      setResultData(false);
      setDefaultTalentValue(null);
      setDataCreateTalent(undefined);
      setProcessingProgress(0);
      toast.error(API_UNKNOWN_ERROR_MESSAGE);
    }
  };

  const onSubmit = async () => {
    const memo = getValues("memo") ?? "";
    if (!checkEmptyData(memo)) return;
    const hasFiles = !!file && file.length > 0;
    if (hasFiles) return await onSubmitScan(memo);
    return await onSubmitNote(memo);
  };

  const onGetValuesCreateTalent = useCallback((data: TTalentForm) => {
    setDataCreateTalent(data);
  }, []);

  const onSetValidationTrigger = useCallback((triggerFn: () => Promise<boolean>) => {
    setValidateForm(() => triggerFn);
  }, []);

  const handleSaveTalent = async () => {
    // Certificate master list must be loaded, otherwise the payload would drop
    // every certificate.
    if (!dataCreateTalent || !specificSkillCertificateData?.length) return;
    try {
      dataCreateTalent.nationalityId = dataCreateTalent.nationalityId === 0 ? undefined : dataCreateTalent.nationalityId;
      const payload = convertTalentFormDataReq(dataCreateTalent, languageTestData as TMasterLanguageTest[], specificSkillCertificateData);
      payload.personInChargeId = Number(user?.data.id) ?? 0;

      // Update mode (IROHANA_MATCH_DEV-281): modal opened from talent detail —
      // overwrite the existing record instead of creating a new one. Preserve
      // the existing recruitmentStatus so we don't accidentally demote a saved
      // talent back to DRAFT (the scan form defaults to DRAFT for new entries).
      if (currentTalent?.id) {
        if (currentTalent.recruitmentStatus != null) {
          payload.recruitmentStatus = currentTalent.recruitmentStatus;
        }
        const { data } = await updateTalentApi(currentTalent.id, payload);
        if (data) {
          toast.success(API_SUCCESS_MESSAGE);
          resetForm();
          modal.close();
          return navigate(`/talent/${currentTalent.id}/edit`, { replace: true });
        } else {
          toast.error(API_UNKNOWN_ERROR_MESSAGE);
        }
        return;
      }

      const { data } = await createTalentApi(payload);
      if (data) {
        toast.success(API_SUCCESS_MESSAGE);
        return navigate(`/talent/${data.id}/edit`, { replace: true });
      } else {
        toast.error(API_UNKNOWN_ERROR_MESSAGE);
      }
      resetForm();
      modal.close();
    } catch (e) {
      showError(e);
    }
  };

  const createTalent = async () => {
    if (!dataCreateTalent) return;
    if (!validateForm) return;
    const isValid = await validateForm();
    if (!isValid) return;

    // Update mode (IROHANA_MATCH_DEV-281): skip duplicate-passport / duplicate-talent
    // checks. The current record is the one being edited, so any "duplicate"
    // match against itself is a false positive and the existence APIs don't
    // know to exclude it.
    if (currentTalent?.id) {
      await handleSaveTalent();
      return;
    }

    if (dataCreateTalent.passportNumber) {
      const passportDuplicate: TCheckPassportDuplicateResDto = await fetchCheckPassportDuplicateApi(undefined, dataCreateTalent.passportNumber);
      if (passportDuplicate?.data?.length > 0) {
        const dataPassportDuplicate = passportDuplicate.data;
        if (dataPassportDuplicate.length > 0) {
          setDuplicatePassports(dataPassportDuplicate);
          setPendingPassportNumber(dataCreateTalent.passportNumber);
          await duplicatePassportModal.show();
          return;
        }
      }
    }

    const talentExists: TGetTalentExistsResDto | null = dataCreateTalent.fullName
      ? await fetchTalentExistsApi(
          null,
          dataCreateTalent.fullName,
          dataCreateTalent.birthday ? dataCreateTalent.birthday?.format(DATE_FORMAT_FOR_API) : null,
        )
      : null;

    if (talentExists?.data && talentExists.data.length > 0) {
      const dataTalentExists = talentExists.data;
      if (dataTalentExists.length > 0) {
        setDuplicateTalents(dataTalentExists);
        await duplicateTalentModal.show();
        return;
      }
    }

    await handleSaveTalent();
  };

  const resetForm = () => {
    setIsSubmit(false);
    setResultData(false);
    setDefaultTalentValue(null);
    setValue("memo", "");
    setDataCreateTalent(undefined);
    setScanResult(null);
    setHasScannedBefore(false);
    setActiveTab("all");
  };

  return {
    // form
    methods,
    control,
    scanImageSchema,
    memoWatchField,
    otherNote,
    isShowDropZoneUpdateFile,
    // Rescan reminder applies only to the edit-from-talent-detail flow
    // (IROHANA_MATCH_DEV-281). In the create-from-list flow there are no
    // existing values to compare against, so the banner stays single-line.
    hasScannedBefore: !!currentTalent && hasScannedBefore,
    // scan state
    isResultData,
    defaultTalentValue,
    file,
    isSubmit,
    previewFile,
    typePreviewFile,
    processingProgress,
    currentFile,
    currentFileIndex,
    totalFiles,
    scanResult,
    activeTab,
    setActiveTab,
    // duplicates
    duplicateTalents,
    duplicatePassports,
    pendingPassportNumber,
    // modals
    confirmLeaveModal,
    duplicateTalentModal,
    duplicatePassportModal,
    // handlers
    handleChangeFiles,
    onClose,
    confirmClose,
    onSubmit,
    cancelScan,
    createTalent,
    handleSaveTalent,
    onGetValuesCreateTalent,
    onSetValidationTrigger,
  };
}

export type UseScanImageStateReturn = ReturnType<typeof useScanImageState>;
